// Small presentation helpers shared by the visitor support chat and the admin
// inbox. Kept dependency-free (no date-fns in this repo) and locale-aware.

type DateInput = Date | string | null | undefined

function toDate(d: DateInput): Date | null {
  if (!d) return null
  return typeof d === `string` ? new Date(d) : d
}

/** "Today" / "Yesterday" / "March 4" — the date-separator label in a thread. */
export function getDateLabel(date: DateInput): string {
  const d = toDate(date)
  if (!d) return `Today`
  const today = new Date()
  if (d.toDateString() === today.toDateString()) return `Today`
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)
  if (d.toDateString() === yesterday.toDateString()) return `Yesterday`
  return d.toLocaleDateString(`en-US`, { month: `long`, day: `numeric` })
}

export function isSameDay(a: DateInput, b: DateInput): boolean {
  const da = toDate(a)
  const db = toDate(b)
  if (!da || !db) return false
  return da.toDateString() === db.toDateString()
}

/** "2:45 PM" — the small per-message time. */
export function formatTime(date: DateInput): string {
  const d = toDate(date)
  if (!d) return ``
  return d.toLocaleTimeString(`en-US`, { hour: `numeric`, minute: `2-digit` })
}

/** "just now" / "5m" / "3h" / "2d" / "Mar 4" — compact relative time for lists. */
export function formatRelative(date: DateInput): string {
  const d = toDate(date)
  if (!d) return ``
  const diff = Date.now() - d.getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return `just now`
  if (mins < 60) return `${mins}m`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h`
  const days = Math.floor(hrs / 24)
  if (days < 7) return `${days}d`
  return d.toLocaleDateString(`en-US`, { month: `short`, day: `numeric` })
}

/** Up to two uppercase initials for an avatar fallback. */
export function initials(label: string): string {
  const parts = label.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return `?`
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}
